import { useState, useRef } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Loader2, X, FileText, Sparkles } from "lucide-react";

const FIELDS = [
  ["tribunale", "Tribunale", "text"], ["rge", "RGE", "text"], ["comune", "Comune", "text"],
  ["provincia", "Provincia", "text"], ["indirizzo", "Indirizzo", "text"], ["tipologia", "Tipologia", "text"],
  ["superficie", "Superficie (mq)", "number"], ["prezzo_base", "Prezzo base €", "number"],
  ["valore_perizia", "Valore perizia €", "number"], ["valore_mercato", "Valore mercato €", "number"],
  ["numero_creditori", "N. creditori", "number"], ["totale_debiti", "Totale debiti €", "number"],
  ["costi_regolarizzazione", "Costi regolarizzazione €", "number"], ["numero_esperimento", "N. esperimento", "number"],
];

export default function UploadPeriziaDialog({ open, onClose, onCreated }) {
  const [phase, setPhase] = useState("upload"); // upload | review
  const [loading, setLoading] = useState(false);
  const [fields, setFields] = useState({});
  const [confidence, setConfidence] = useState({});
  const [note, setNote] = useState("");
  const [extras, setExtras] = useState({ stato_occupazionale: "esecutato", abusi_presenti: false, piena_proprieta_esecutato: true, liquidita_zona: "media", qualita_comune: "media", domanda_zona: "media" });
  const fileRef = useRef();

  if (!open) return null;

  const reset = () => { setPhase("upload"); setFields({}); setConfidence({}); setNote(""); };
  const close = () => { reset(); onClose(); };

  const handleFile = async (file) => {
    if (!file) return;
    setLoading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/analyze-pdf", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const c = data.campi || {};
      setFields(Object.fromEntries(Object.entries(c).filter(([, v]) => v != null)));
      setConfidence(data.confidenza || {});
      setNote(data.note_estrazione || "");
      if (c.stato_occupazionale) setExtras((e) => ({ ...e, stato_occupazionale: c.stato_occupazionale }));
      if (c.abusi_presenti != null) setExtras((e) => ({ ...e, abusi_presenti: c.abusi_presenti }));
      if (c.piena_proprieta_esecutato != null) setExtras((e) => ({ ...e, piena_proprieta_esecutato: c.piena_proprieta_esecutato }));
      setPhase("review");
      toast.success("Perizia analizzata dall'AI");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Errore nell'analisi");
    } finally {
      setLoading(false);
    }
  };

  const create = async () => {
    setLoading(true);
    try {
      const payload = { ...fields, ...extras };
      ["superficie", "prezzo_base", "valore_perizia", "valore_mercato", "numero_creditori", "totale_debiti", "costi_regolarizzazione", "numero_esperimento"].forEach((k) => {
        if (payload[k] != null) payload[k] = Number(payload[k]);
      });
      const { data } = await api.post("/deals", { data: payload });
      toast.success("Operazione creata e qualificata");
      close();
      onCreated(data.id);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Errore nella creazione");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-[#0A1826] border border-white/10 rounded-sm px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#EA7B2B]";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="upload-dialog">
      <div className="w-full max-w-3xl max-h-[88vh] overflow-y-auto bg-[#122A3F] border border-white/10 rounded-sm">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 sticky top-0 bg-[#122A3F] z-10">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#EA7B2B]" />
            <h2 className="font-display text-lg text-white">Analisi perizia con AI</h2>
          </div>
          <button onClick={close} data-testid="upload-close"><X className="w-5 h-5 text-zinc-500 hover:text-white" /></button>
        </div>

        <div className="p-6">
          {phase === "upload" && (
            <div>
              <div
                onClick={() => fileRef.current?.click()}
                className="border-2 border-dashed border-white/15 rounded-sm p-12 text-center cursor-pointer hover:border-[#EA7B2B]/50 transition-colors"
                data-testid="upload-dropzone"
              >
                {loading ? (
                  <div className="flex flex-col items-center gap-3">
                    <Loader2 className="w-10 h-10 text-[#EA7B2B] animate-spin" />
                    <p className="text-sm text-zinc-400">Claude Sonnet 4.6 sta leggendo la perizia…</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-3">
                    <Upload className="w-10 h-10 text-zinc-500" />
                    <p className="text-sm text-white">Carica la perizia in PDF</p>
                    <p className="text-xs text-zinc-500">L'AI estrarrà automaticamente i campi chiave dell'operazione</p>
                  </div>
                )}
                <input ref={fileRef} type="file" accept="application/pdf" className="hidden" data-testid="upload-input" onChange={(e) => handleFile(e.target.files[0])} />
              </div>
            </div>
          )}

          {phase === "review" && (
            <div className="space-y-5">
              {note && (
                <div className="text-xs text-[#D97706] bg-[#D97706]/10 border border-[#D97706]/30 rounded-sm px-3 py-2 flex items-start gap-2">
                  <FileText className="w-4 h-4 shrink-0 mt-0.5" /> <span>{note}</span>
                </div>
              )}
              <p className="text-sm text-zinc-400">Verifica e correggi i dati estratti prima di qualificare l'operazione. I dati incerti sono evidenziati.</p>
              <div className="grid grid-cols-2 gap-4">
                {FIELDS.map(([key, label, type]) => {
                  const conf = confidence[key];
                  const low = conf != null && conf < 0.6;
                  return (
                    <div key={key}>
                      <label className="text-xs text-zinc-400 flex items-center gap-2">
                        {label}
                        {low && <span className="text-[10px] text-[#D97706]">da verificare</span>}
                      </label>
                      <input
                        type={type}
                        value={fields[key] ?? ""}
                        onChange={(e) => setFields((f) => ({ ...f, [key]: e.target.value }))}
                        data-testid={`field-${key}`}
                        className={`mt-1 ${inputCls} ${low ? "border-[#D97706]/50" : ""}`}
                      />
                    </div>
                  );
                })}
                <div>
                  <label className="text-xs text-zinc-400">Occupazione</label>
                  <select value={extras.stato_occupazionale} onChange={(e) => setExtras((x) => ({ ...x, stato_occupazionale: e.target.value }))} className={`mt-1 ${inputCls}`} data-testid="field-occupazione">
                    <option value="libero">Libero</option>
                    <option value="esecutato">Occupato esecutato</option>
                    <option value="terzi">Terzi senza titolo</option>
                    <option value="terzi_con_titolo">Terzi con titolo</option>
                  </select>
                </div>
                <div className="flex items-end gap-4">
                  <label className="flex items-center gap-2 text-xs text-zinc-400"><input type="checkbox" checked={extras.abusi_presenti} onChange={(e) => setExtras((x) => ({ ...x, abusi_presenti: e.target.checked }))} /> Abusi presenti</label>
                  <label className="flex items-center gap-2 text-xs text-zinc-400"><input type="checkbox" checked={extras.piena_proprieta_esecutato} onChange={(e) => setExtras((x) => ({ ...x, piena_proprieta_esecutato: e.target.checked }))} /> Piena proprietà</label>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button onClick={() => setPhase("upload")} className="px-4 py-2 text-sm text-zinc-400 border border-white/10 rounded-sm hover:bg-white/5">Indietro</button>
                <button onClick={create} disabled={loading} data-testid="create-deal-btn" className="flex items-center gap-2 px-5 py-2 text-sm font-medium bg-[#EA7B2B] text-black rounded-sm hover:bg-[#F59E4A] disabled:opacity-60">
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />} Crea e qualifica
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
